import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { SortOption, FreelancerSearchResult } from '../types/search';
import { AlgoliaFreelancerStrategy } from '../services/search/strategies/AlgoliaSearchStrategy';
import { SupabaseFreelancerStrategy } from '../services/search/strategies/SupabaseSearchStrategy';

interface SearchParams {
  keyword?: string;
  filter_languages?: string[];
  filter_service_category_ids?: number[];
  filter_city_ids?: number[];
  sort_order?: SortOption[];
  limit_count?: number;
  offset_count?: number;
  filter_rating?: number | null;
  filter_proximity?: number | null;
  origin_lat?: number | null;
  origin_lon?: number | null;
}

interface SearchStoreState {
  results: FreelancerSearchResult[];
  spotlightedResults: FreelancerSearchResult[];
  suggestions: string[];
  searchingFreelancersResult: FreelancerSearchResult[];
  error: string | null;
  spotlightedError: string | null;
  suggestionsError: string | null;
  suggestionsLoading: boolean;
  spotlightedLoading: boolean;
  freelancerSearchingLoading: boolean;
  searchingError: string | null;
  hasMore: boolean;
  lastSearchParams: SearchParams | null;
  search_freelancers: (params: SearchParams) => Promise<void>;
  fetch_more_freelancers: () => Promise<void>;
  fetch_spotlighted_freelancers: (
    origin_lat?: number | null,
    origin_lon?: number | null
  ) => Promise<void>;
  fetch_suggestions: (keyword: string) => Promise<void>;
  clearSuggestions: () => void;
  fetch_searching_freelancers: (keyword: string) => Promise<void>;
  setSearchingLoading: (loading: boolean) => void;
  setSpotlightedLoading: (loading: boolean) => void;
}

const algoliaStrategy = new AlgoliaFreelancerStrategy();
const supabaseStrategy = new SupabaseFreelancerStrategy();

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Unknown error';
};

type RawFreelancerSearchResult = Omit<FreelancerSearchResult, 'last_name'> & {
  last_name?: unknown;
};

const normalizeFreelancerResults = (
  results: RawFreelancerSearchResult[]
): FreelancerSearchResult[] =>
  results.map(result => ({
    ...result,
    last_name: typeof result.last_name === 'string' ? result.last_name : null,
  }));

const createSearchStore = () =>
  create<SearchStoreState>((set, get) => {
    async function fetchAndSetResults(params: SearchParams, append = false) {
      if (get().freelancerSearchingLoading) return;

      const {
        keyword = '',
        filter_languages = [],
        filter_service_category_ids = [],
        filter_city_ids = [],
        limit_count = 20,
        offset_count = 0,
        origin_lat = null,
        origin_lon = null,
        filter_rating = null,
        filter_proximity = null,
        sort_order = [],
      } = params;

      // Reset results if no search criteria
      if (
        !keyword.trim() &&
        filter_languages.length === 0 &&
        filter_service_category_ids.length === 0 &&
        filter_city_ids.length === 0 &&
        !filter_rating &&
        !filter_proximity &&
        sort_order.length === 0
      ) {
        set({ results: [], freelancerSearchingLoading: false, error: null });
        return;
      }

      set({ freelancerSearchingLoading: true, error: null });

      try {
        const { results, hasMore } = await supabaseStrategy.search({
          keyword,
          filter_languages,
          filter_service_category_ids,
          filter_city_ids,
          limit_count,
          offset_count,
          origin_lat,
          origin_lon,
          filter_rating,
          filter_proximity,
          sort_order,
        });

        set(state => ({
          results: append ? [...state.results, ...results] : results,
          freelancerSearchingLoading: false,
          hasMore,
          lastSearchParams: { ...params, limit_count, offset_count },
        }));
      } catch (err: unknown) {
        console.error('Search error:', err);
        set({ error: getErrorMessage(err), freelancerSearchingLoading: false, hasMore: false });
      }
    }

    return {
      results: [],
      spotlightedResults: [],
      suggestions: [],
      searchingFreelancersResult: [],
      error: null,
      spotlightedError: null,
      suggestionsError: null,
      suggestionsLoading: false,
      spotlightedLoading: false,
      freelancerSearchingLoading: false,
      searchingError: null,
      hasMore: false,
      lastSearchParams: null,

      search_freelancers: params => fetchAndSetResults(params, false),
      fetch_more_freelancers: async () => {
        const state = get();
        const params = state.lastSearchParams;
        if (!params) return Promise.resolve();
        return fetchAndSetResults({ ...params, offset_count: state.results.length }, true);
      },

      async fetch_spotlighted_freelancers(origin_lat = null, origin_lon = null) {
        if (get().spotlightedLoading) return;
        set({ spotlightedLoading: true, spotlightedError: null });
        const { data, error } = await supabase.rpc('fetch_spotlighted_freelancers', {
          limit_count: 20,
          offset_count: 0,
          origin_lat,
          origin_lon,
        });
        if (error) {
          set({ spotlightedError: error.message, spotlightedLoading: false });
        } else {
          set({ spotlightedResults: data || [], spotlightedLoading: false });
        }
      },

      async fetch_suggestions(keyword: string) {
        if (!keyword || keyword.length < 2) {
          set({ suggestions: [], suggestionsLoading: false });
          return;
        }
        set({ suggestionsLoading: true, suggestionsError: null });
        try {
          const suggestions = await algoliaStrategy.fetchSuggestions(keyword);
          set({ suggestions, suggestionsLoading: false });
        } catch (err: unknown) {
          set({ suggestionsError: getErrorMessage(err), suggestionsLoading: false });
        }
      },

      clearSuggestions: () =>
        set({ suggestions: [], suggestionsError: null, suggestionsLoading: false }),

      async fetch_searching_freelancers(keyword: string) {
        if (!keyword || keyword.length < 3) {
          set({ searchingFreelancersResult: [], freelancerSearchingLoading: false });
          return;
        }
        set({ freelancerSearchingLoading: true, searchingError: null });
        try {
          const { results } = await algoliaStrategy.search({
            keyword,
            limit_count: 5,
            offset_count: 0,
          });
          set({
            searchingFreelancersResult: normalizeFreelancerResults(results),
            freelancerSearchingLoading: false,
          });
        } catch (err: unknown) {
          set({ searchingError: getErrorMessage(err), freelancerSearchingLoading: false });
        }
      },

      setSearchingLoading: loading => set({ freelancerSearchingLoading: loading }),
      setSpotlightedLoading: loading => set({ spotlightedLoading: loading }),
    };
  });

export const useFreelancerSearchStore = createSearchStore();
export const useAllianceSearchStore = createSearchStore();
